<template>
    <InertiaHead>
        <title>{{ title }}</title>
        <slot name="meta"></slot>
    </InertiaHead>

    <div class="h-screen flex overflow-hidden bg-gray-100">
        <TransitionRoot as="template" :show="sidebarOpen">
            <Dialog as="div" static class="fixed inset-0 flex z-40 md:hidden" @close="sidebarOpen = false" :open="sidebarOpen">
                <TransitionChild as="template" enter="transition-opacity ease-linear duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="transition-opacity ease-linear duration-300" leave-from="opacity-100" leave-to="opacity-0">
                    <DialogOverlay class="fixed inset-0 bg-gray-600 bg-opacity-75" />
                </TransitionChild>
                <TransitionChild as="template" enter="transition ease-in-out duration-300 transform" enter-from="-translate-x-full" enter-to="translate-x-0" leave="transition ease-in-out duration-300 transform" leave-from="translate-x-0" leave-to="-translate-x-full">
                    <div class="relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-secondary">
                        <TransitionChild as="template" enter="ease-in-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in-out duration-300" leave-from="opacity-100" leave-to="opacity-0">
                            <div class="absolute top-0 right-0 -mr-12 pt-2">
                                <button type="button" class="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white" @click="sidebarOpen = false">
                                    <span class="sr-only">Close sidebar</span>
                                    <XIcon class="h-6 w-6 text-white" aria-hidden="true" />
                                </button>
                            </div>
                        </TransitionChild>
                        <div class="flex-shrink-0 flex items-center px-4">
                            <img src="/media/logo.png" class="h-16 w-auto" />
                        </div>
                        <div class="mt-10 flex-1 h-0 overflow-y-auto">
                            <nav class="px-2 space-y-1">
                                <template v-for="(item, index) in navigation" :key="item.name">
                                    <template v-if="$hasRole(item.roles)">
                                        <div
                                            v-if="item.group"
                                            class="px-2 mr-4 text-xs font-semibold text-gray-400 uppercase tracking-wider pb-2"
                                            :class="{ 'pt-5' : index > 0 }"
                                        >
                                            {{ item.name }}
                                        </div>

                                        <InertiaLink v-else :href="item.href" :class="[item.current ? 'bg-primary text-secondary' : 'text-gray-100 hover:bg-primary hover:text-secondary', 'group flex items-center px-2 py-2 text-base font-medium rounded-md']">
                                            <component :is="item.icon" class="mr-4 flex-shrink-0 h-6 w-6 text-gray-50 group-hover:text-secondary" aria-hidden="true" />
                                            {{ item.name }}
                                        </InertiaLink>
                                    </template>
                                </template>
                            </nav>
                        </div>
                    </div>
                </TransitionChild>
                <div class="flex-shrink-0 w-14" aria-hidden="true"></div>
            </Dialog>
        </TransitionRoot>

        <!-- Static sidebar for desktop -->
        <div class="hidden bg-secondary bg-pattern md:flex md:flex-shrink-0">
            <div class="flex flex-col w-64">
                <!-- Sidebar component, swap this element with another sidebar if you like -->
                <div class="flex flex-col flex-grow pt-5 pb-4 overflow-y-auto">
                    <div class="flex items-center flex-shrink-0 px-4 justify-center mt-2">
                        <img src="/media/logo.png" class="h-16 w-auto" />
                    </div>
                    <div class="mt-8 flex-1 flex flex-col">
                        <nav class="flex-1 px-2 space-y-1">
                            <template v-for="(item, index) in navigation" :key="item.name">
                                <template v-if="$hasRole(item.roles)">
                                    <div
                                        v-if="item.group"
                                        class="px-2 mr-4 text-xs font-semibold text-gray-400 uppercase tracking-wider pb-2"
                                        :class="{ 'pt-5' : index > 0 }"
                                    >
                                        {{ item.name }}
                                    </div>

                                    <InertiaLink v-else :href="item.href" :class="[item.current ? 'bg-primary text-secondary' : 'text-gray-100 hover:bg-primary hover:text-secondary', 'group flex items-center px-2 py-2 text-sm font-medium rounded-md']">
                                        <component :is="item.icon" class="mr-3 flex-shrink-0 h-6 w-6 text-gray-50 group-hover:text-secondary" aria-hidden="true" />
                                        {{ item.name }}
                                    </InertiaLink>
                                </template>
                            </template>
                        </nav>
                    </div>
                </div>
            </div>
        </div>
        <div class="flex flex-col w-0 flex-1 overflow-hidden">
            <div class="relative z-5 flex-shrink-0 flex h-16 bg-white shadow">
                <button type="button" class="px-4 border-r border-gray-200 text-gray-500 focus:outline-none md:hidden" @click="sidebarOpen = true">
                    <span class="sr-only">Open sidebar</span>
                    <MenuAlt2Icon class="h-6 w-6" aria-hidden="true" />
                </button>
                <div class="flex-1 px-8 flex justify-between">
                    <div class="flex-1 flex items-center">
                        <h1 class="text-xl font-base text-gray-900">
                            <slot name="title"></slot>
                        </h1>
                        <div class="pl-4">
                            <slot name="titleNav"></slot>
                        </div>
                    </div>
                    <div class="ml-4 flex items-center md:ml-6">

                        <!-- Profile dropdown -->
                        <Menu as="div" class="ml-3 relative">
                            <div>
                                <MenuButton class="max-w-xs bg-white flex items-center text-sm rounded-full focus:outline-none">
                                    <span class="sr-only">Open user menu</span>
                                    <span class="h-8 w-8 leading-8 rounded-full text-center bg-secondary text-white">{{ user.initials }}</span>
                                </MenuButton>
                            </div>
                            <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
                                <MenuItems class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                                    <MenuItem v-for="item in userNavigation" :key="item.name" v-slot="{ active }">
                                        <InertiaLink :href="item.href" :method="item.method ?? 'get'" :as="item.method === 'post' ? 'button' : 'a'" :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700']" class="w-full text-left">{{ item.name }}</InertiaLink>
                                    </MenuItem>
                                </MenuItems>
                            </transition>
                        </Menu>
                    </div>
                </div>
            </div>

            <main class="flex-1 relative h-[calc(100vh-64px)] overflow-x-hidden overflow-y-auto focus:outline-none">
                <slot></slot>

                <!-- Global notification live region, render this permanently at the end of the document -->
                <div aria-live="assertive" class="fixed inset-0 flex items-end p-4 z-30 pointer-events-none sm:items-start">
                    <div class="w-full flex flex-col items-center space-y-4 sm:items-end">

                        <Notification
                            v-if="$page.props.flash.success"
                            @closed="$page.props.flash.success = null"
                        >
                            {{ $page.props.flash.success }}
                        </Notification>

                    </div>
                </div>
            </main>

        </div>
    </div>
</template>

<script>
import {
    Dialog,
    DialogOverlay,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    TransitionChild,
    TransitionRoot,
} from '@headlessui/vue'
import {
    BellIcon,
    CalendarIcon,
    CollectionIcon,
    CogIcon,
    DesktopComputerIcon,
    GlobeIcon,
    MenuAlt2Icon,
    OfficeBuildingIcon,
    ShieldCheckIcon,
    TranslateIcon,
    UserGroupIcon,
    UsersIcon,
    XIcon,
} from '@heroicons/vue/outline'
import Notification from "@/Components/Notification";

export default {
    components: {
        Notification,
        Dialog,
        DialogOverlay,
        Menu,
        MenuButton,
        MenuItem,
        MenuItems,
        TransitionChild,
        TransitionRoot,
        BellIcon,
        DesktopComputerIcon,
        GlobeIcon,
        MenuAlt2Icon,
        ShieldCheckIcon,
        TranslateIcon,
        UsersIcon,
        XIcon,
    },
    props: {
        title: String,
    },
    data() {
        return {
            navigation: [
                {
                    name: this.__('admin::nav.group_projects'),
                    group: true,
                    roles: ['admin'],
                },
                {
                    name: this.__('admin::nav.projects'),
                    href: this.$route('admin::projects.index'),
                    icon: CollectionIcon,
                    roles: ['admin'],
                },
                {
                    name: this.__('admin::nav.group_global'),
                    group: true,
                    roles: ['admin'],
                },
                {
                    name: this.__('admin::nav.departments'),
                    href: this.$route('admin::departments.index'),
                    icon: OfficeBuildingIcon,
                    roles: ['admin'],
                },
                {
                    name: this.__('admin::nav.realtors'),
                    href: this.$route('admin::realtors.index'),
                    icon: UserGroupIcon,
                    roles: ['admin'],
                },
                {
                    name: this.__('admin::nav.disallowed-dates'),
                    href: this.$route('admin::disallowed-dates.edit'),
                    icon: CalendarIcon,
                    roles: ['admin'],
                },
                {
                    name: this.__('admin::nav.alerts'),
                    href: this.$route('admin::alerts.index'),
                    icon: BellIcon,
                    roles: ['admin'],
                },
                {
                    name: this.__('admin::nav.group_content'),
                    group: true,
                    roles: ['admin'],
                },
                {
                    name: this.__('admin::nav.settings'),
                    href: this.$route('admin::settings.index'),
                    icon: CogIcon,
                    roles: ['admin'],
                },
                {
                    name: this.__('admin::nav.texts'),
                    href: this.$route('admin::language-lines.index'),
                    icon: TranslateIcon,
                    roles: ['admin'],
                },
                {
                    name: this.__('admin::nav.group_access'),
                    group: true,
                    roles: ['admin'],
                },
                {
                    name: this.__('admin::nav.users'),
                    href: this.$route('admin::users.index'),
                    icon: ShieldCheckIcon,
                    roles: ['admin'],
                },
            ],
            userNavigation: [
                {
                    name: this.__('admin::nav.my_profile'),
                    href: this.$route('admin::profile.edit'),
                },
                {
                    name: this.__('admin::nav.sign_out'),
                    href: this.$route('admin::auth.destroy'),
                    method: 'post',
                },
            ],
            user: this.$page.props.auth.user,
            sidebarOpen: false,
        };
    },
}
</script>
